import React, {
  useEffect,
  useState,
  createContext,
  useMemo,
  useCallback,
} from "react";
import ProductsHeader from "./components/ProductsHeader";
import CategoryList from "./components/CategoryList";
import CartHeader from "./components/CartHeader";
import CartTable from "./components/CartTable";
import Products from "./components/Products";
import ChatPopup from "./components/ChatPopup";
import useDefaultCurrency from "../utility/useDefaultCurrency";
import SearchandCustomer from "./components/SearchandCustomer";
import CustomerIdProvider from "../context/CustomerIdProvider";
import AllProductProvider from "../context/AllProductProvider";
import AutoBillProvider from "../context/AutoBillProvider";
import OfflineBanner from "./components/OfflineBanner";

export const CustomerContext = createContext();

function CashierHomepage() {
  const { data } = useDefaultCurrency("cashier");
  const [currency, setCurrency] = useState(null);

  const [categoryId, setCategoryId] = useState(null);
  const [searched, setSearched] = useState("");
  const [openDraft, setOpenDraft] = useState(false);
  const [openMessage, setOpenMessage] = useState(false);
  const [barcodeScan, setBarcodeScan] = useState(false);
  const [openGeneratePassword, setOpenGeneratePassword] = useState(false);
  const [selectedCustomerName, setSelectedCustomerName] = useState("");
  const [newCartData, setNewCartData] = useState(null);
  const [updateResumeStatus, setUpdateResumeStatus] = useState(false);
  const [draftId, setDraftId] = useState(null);
  const [totalUnreadMessages, setTotalUnreadMessages] = useState(0);

  // Memoized functions to prevent re-creation
  const setCategoryIdCallback = useCallback((id) => setCategoryId(id), []);
  const setSearchedCallback = useCallback((term) => setSearched(term), []);

  // Memoized Context to Prevent Unnecessary Re-Renders
  const contextValue = useMemo(
    () => ({
      newCartData,
      setNewCartData,
      selectedCustomerName,
      setSelectedCustomerName,
      searched,
      setSearched,
      openDraft,
      setOpenDraft,
      openMessage,
      setOpenMessage,
      barcodeScan,
      setBarcodeScan,
      openGeneratePassword,
      setOpenGeneratePassword,
      updateResumeStatus,
      setUpdateResumeStatus,
      draftId,
      setDraftId,
      totalUnreadMessages,
      setTotalUnreadMessages,
      currency,
    }),
    [
      newCartData,
      selectedCustomerName,
      searched,
      openDraft,
      openMessage,
      barcodeScan,
      openGeneratePassword,
      updateResumeStatus,
      draftId,
      totalUnreadMessages,
      currency,
    ]
  );

  // Prevent unnecessary re-renders for currency updates
  useEffect(() => {
    if (data?.currency?.symbol && data?.currency?.symbol !== currency) {
      setCurrency(data.currency.symbol);
    }
  }, [data?.currency?.symbol, currency]); // Only depend on `symbol`

  return (
    <CustomerContext.Provider value={contextValue}>
      <CustomerIdProvider>
        <AllProductProvider>
          <AutoBillProvider>
            <div className="flex flex-row w-full h-screen lg:overflow-y-hidden">
              {/* Categories & Products Section */}
              <div className="flex-grow w-8/12 h-full">
                <OfflineBanner />
                <ProductsHeader />
                <SearchandCustomer
                  searched={searched}
                  setSearched={setSearchedCallback}
                />
                <CategoryList setCategoryId={setCategoryIdCallback} />
                <Products categoryId={categoryId} searched={searched} />
              </div>

              {/* Cart Section */}
              <div className="w-4/12 py-2 bg-black text-white">
                <CartHeader />
                <CartTable />
                <ChatPopup
                  openMessage={openMessage}
                  setOpenMessage={setOpenMessage}
                />
              </div>
            </div>
          </AutoBillProvider>
        </AllProductProvider>
      </CustomerIdProvider>
    </CustomerContext.Provider>
  );
}

export default React.memo(CashierHomepage);
